import { cogProtocol } from "@geomatico/maplibre-cog-protocol";
import MapLibreGL, {
  FlyToOptions,
  LayerSpecification,
  SpriteSpecification,
  StyleSpecification,
} from "maplibre-gl";
import { Protocol as PmtilesProtocol } from "pmtiles";
import { useMemo, useRef, useState } from "react";
import { ImmutableLike, useMap } from "react-map-gl/maplibre";
import { MapProps } from "react-map-gl/maplibre";

import { useFrontendSetting } from "@nens/ranalib";
import { useOnlyOnce, useRunOnceWhen } from "@nens/react/hooks";
import { useStyleJson } from "@nens/react-map-gl/utils";

import { BEFORE_BUILDING_LAYER_ID, BUILDING_LAYER_ID } from "../consts/building-layer-ids";
import { useRanaMapBackground } from "../utils/background";

export const TERRAIN_SOURCE = "terrainSource";
export const HILLSHADE_SOURCE = "hillshadeSource";

export type MapStyle = Exclude<
  Exclude<Exclude<MapProps["mapStyle"], string>, undefined>,
  ImmutableLike<object>
>;

export const AFTER_LABEL_LAYER_ID = "after-label-layer";

export function useRanaMap(mapId: string, sprites?: SpriteSpecification) {
  const { [mapId]: map } = useMap();
  const [isThreeD, setIsThreeD] = useState(false);
  useRunOnceWhen(!!map, () => map && setIsThreeD(map?.getPitch() > 0)); // Set initial state based on map pitch

  // Controls both the 3DBAG 3D buildings layer and MapLibre's terrain elevation mesh together:
  // the buildings rely on real terrain elevation to sit on the ground correctly (see
  // rana-3d-buildings-layer.tsx), so they are only ever shown while terrain is also enabled.
  const [show3DBuildings, setShow3DBuildings] = useState(false);

  const { url } = useRanaMapBackground(mapId);

  const mapstyleWithBuildings: Partial<MapStyle> | null = useStyleJson(url, false, sprites);
  const { maptilerApiKey } = useFrontendSetting();

  // Once the mapStyle has loaded it should stay available to prevent errrors and warnings
  const lastMapStyleRef = useRef<typeof mapstyleWithBuildings | undefined>(undefined);
  const mapStyle: StyleSpecification | null = useMemo(() => {
    const newMapStyle = mapstyleWithBuildings ?? lastMapStyleRef.current;
    if (!newMapStyle) return null;
    if (mapstyleWithBuildings) lastMapStyleRef.current = mapstyleWithBuildings;

    const noBuildingLayers =
      newMapStyle?.layers?.filter(({ id }) => id !== BUILDING_LAYER_ID) ?? [];

    const isLabelLayer = (layer: LayerSpecification) =>
      layer.type === "symbol" || /label|text|place/i.test(layer.id);

    const labelLayers = noBuildingLayers.filter(isLabelLayer);
    const otherLayers = noBuildingLayers.filter((layer) => !isLabelLayer(layer));

    // Insert an empty layer before the labels so we can set the building layer beforeId on that.
    const layers: LayerSpecification[] = [
      ...otherLayers,
      { id: BEFORE_BUILDING_LAYER_ID, type: "background", layout: { visibility: "none" } },
      ...labelLayers,
      { id: AFTER_LABEL_LAYER_ID, type: "background", layout: { visibility: "none" } },
    ];

    const styleObj: StyleSpecification = {
      version: 8,
      sources: {},
      ...newMapStyle,
      glyphs: maptilerApiKey
        ? `https://api.maptiler.com/fonts/{fontstack}/{range}.pbf?key=${maptilerApiKey}`
        : newMapStyle.glyphs, // Use Maptiler fonts for all map styles (BGT does not include Roboto for example)
      layers,
    };

    // Only add terrain while 3D buildings are shown
    // FUTURE: Also add hillshade? Last try of adding the hillshade made the maps style never stop loading
    if (show3DBuildings) {
      styleObj.sources[TERRAIN_SOURCE] = {
        type: "raster-dem",
        url: `https://terrain.reearth.land/mapbox/ellipsoid/tilejson.json`,
        tileSize: 256,
      };
      styleObj.terrain = {
        source: TERRAIN_SOURCE,
        exaggeration: 1,
      };
    }

    return styleObj;
    // Sprites.length is added as a dependency to ensure the mapStyle updates when sprites change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapstyleWithBuildings, sprites?.length, maptilerApiKey, show3DBuildings]);

  const flyTo = (options: FlyToOptions) => {
    if (!map) return;
    map.flyTo({ ...options, maxDuration: 1000 });
  };

  // Add pmtiles protocol to serve pmtiles from the backend
  useOnlyOnce(() => {
    const pmtilesProtocol = new PmtilesProtocol();
    MapLibreGL.addProtocol("cog", cogProtocol);
    MapLibreGL.addProtocol("pmtiles", pmtilesProtocol.tile);
    return () => {
      MapLibreGL.removeProtocol("cog");
      MapLibreGL.removeProtocol("pmtiles");
    };
  });

  return {
    mapStyle,
    isThreeD,
    setIsThreeD,
    setThreeD: () => flyTo({ pitch: isThreeD ? 0 : 45 }),
    zoomIn: () => map && flyTo({ zoom: map.getZoom() + 1 }),
    zoomOut: () => map && flyTo({ zoom: map.getZoom() - 1 }),
    toFlat: () => flyTo({ pitch: 0, bearing: 0 }),
    show3DBuildings,
    setShow3DBuildings,
  } as const;
}
